import { Menu, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getHeroExpansion } from './hero-expansion';
import { OpeningCard } from './OpeningCard';
import {
  Brand,
  PrimaryCta,
  PLATFORM_URL,
  ConversionHero,
  HowItWorksSection,
  ResourcesSection,
  BusinessSection,
  PlansSection,
  FaqSection,
  Footer,
} from './LandingSections';

type HeroPhase = 'entering' | 'row' | 'grid' | 'expanding' | 'settled';
const heroCardPlaceholders = Array.from(
  { length: 10 },
  (_, index) => `hero-card-${index + 1}`,
);
const navigation = [
  { label: 'Cartões', href: '#cartoes' },
  { label: 'Recursos', href: '#recursos' },
  { label: 'Planos', href: '#planos' },
];

function App() {
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [heroPhase, setHeroPhase] = useState<HeroPhase>('entering');
  const cardStageRef = useRef<HTMLDivElement>(null);
  const expansionRef = useRef<HTMLDivElement>(null);
  const heroSettled = heroPhase === 'settled';

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [menuOpen]);

  // The entrance owns the whole viewport: nothing below it is reachable until it lands.
  useEffect(() => {
    if (heroSettled) return undefined;
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => {
      root.style.overflow = previous;
    };
  }, [heroSettled]);

  useEffect(() => {
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setHeroPhase('settled');
      return undefined;
    }
    const rowTimeout = window.setTimeout(() => setHeroPhase('row'), 50);
    const gridTimeout = window.setTimeout(() => setHeroPhase('grid'), 620);
    const expandTimeout = window.setTimeout(
      () => setHeroPhase('expanding'),
      3140,
    );
    return () => {
      window.clearTimeout(rowTimeout);
      window.clearTimeout(gridTimeout);
      window.clearTimeout(expandTimeout);
    };
  }, []);

  useLayoutEffect(() => {
    if (heroPhase !== 'expanding') return;
    const stage = cardStageRef.current;
    const surface = expansionRef.current;
    if (!stage || !surface) return;

    const expansion = getHeroExpansion(
      stage.getBoundingClientRect(),
      [
        ...stage.querySelectorAll(
          '.hero-card-row[data-row="2"] .hero-card-silhouette',
        ),
      ].map((card) => card.getBoundingClientRect()),
    );
    if (!expansion || typeof surface.animate !== 'function') {
      setHeroPhase('settled');
      return;
    }

    Object.assign(surface.style, {
      left: `${expansion.left}px`,
      top: `${expansion.top}px`,
      width: `${expansion.width}px`,
      height: `${expansion.height}px`,
      borderRadius: `${expansion.radius}px`,
    });

    // A short beat identifies the source, then the entire card grows into the hero.
    const animation = surface.animate(
      [
        { transform: 'translate3d(0, 0, 0) scale(1)', offset: 0 },
        {
          transform: 'translate3d(0, 0, 0) scale(1)',
          offset: 0.12,
          easing: 'cubic-bezier(0.65, 0, 0.2, 1)',
        },
        {
          transform: `translate3d(${expansion.translateX}px, ${expansion.translateY}px, 0) scale(${expansion.scale})`,
          opacity: 1,
          offset: 0.78,
        },
        {
          transform: `translate3d(${expansion.translateX}px, ${expansion.translateY}px, 0) scale(${expansion.scale})`,
          opacity: 0,
          offset: 1,
        },
      ],
      { duration: 1000, fill: 'both' },
    );
    const finish = () => setHeroPhase('settled');
    animation.onfinish = finish;
    // A viewport change must never leave part of the white stage exposed.
    window.addEventListener('resize', finish, { once: true });
    return () => {
      animation.onfinish = null;
      animation.cancel();
      window.removeEventListener('resize', finish);
    };
  }, [heroPhase]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="site-shell">
      {heroSettled && (
        <>
          <a className="skip-link" href="#cartoes">
            Ir para o conteúdo
          </a>
          <header className="site-header" data-testid="site-header">
            <nav
              className="nav-wrap page-width"
              aria-label="Navegação principal"
            >
              <Brand />
              <div
                className={`nav-panel ${menuOpen ? 'nav-panel--open' : ''}`}
                id="main-navigation"
              >
                <div className="nav-links">
                  {navigation.map((item) => (
                    <a href={item.href} key={item.label} onClick={closeMenu}>
                      {item.label}
                    </a>
                  ))}
                </div>
                <div className="nav-actions">
                  <a className="nav-login" href={PLATFORM_URL}>
                    Entrar
                  </a>
                  <PrimaryCta className="button--nav" />
                </div>
              </div>
              <button
                className="menu-toggle"
                ref={menuButtonRef}
                type="button"
                aria-controls="main-navigation"
                aria-expanded={menuOpen}
                aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
                onClick={() => setMenuOpen((value) => !value)}
              >
                {menuOpen ? (
                  <X size={22} aria-hidden="true" />
                ) : (
                  <Menu size={22} aria-hidden="true" />
                )}
              </button>
            </nav>
          </header>
        </>
      )}
      <main>
        <section
          className={`hero ${heroSettled ? 'hero--settled' : ''}`}
          id="produto"
          data-testid="animated-hero"
          data-animation-state={heroSettled ? 'settled' : 'intro'}
          data-hero-phase={heroPhase}
          data-transition-layer="foreground"
        >
          <div className="hero-grid" aria-hidden="true" />
          <div
            className="hero-card-stage"
            ref={cardStageRef}
            data-testid="hero-card-stage"
            role="img"
            aria-label={
              heroPhase === 'entering' || heroPhase === 'row'
                ? 'Uma fileira de cartões digitais atravessando o centro'
                : heroPhase === 'grid'
                  ? 'Três fileiras parciais de cartões digitais em movimento'
                  : heroPhase === 'expanding'
                    ? 'Um cartão cresce para preencher a área principal'
                    : 'Hero de fidelização pronta'
            }
          >
            {!heroSettled && (
              <>
                <div className="hero-card-wall" aria-hidden="true">
                  {Array.from({ length: 3 }, (_, rowIndex) => (
                    <div
                      className="hero-card-row"
                      data-row={rowIndex + 1}
                      key={rowIndex}
                    >
                      {heroCardPlaceholders.map((cardId, cardIndex) => (
                        <div className="hero-card-silhouette" key={cardId}>
                          <OpeningCard index={1 + cardIndex + rowIndex * heroCardPlaceholders.length} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
            {heroPhase === 'expanding' && (
              <div
                className="hero-card-expansion"
                ref={expansionRef}
                aria-hidden="true"
              />
            )}
          </div>
          {(heroSettled || heroPhase === 'expanding') && <ConversionHero />}
        </section>

        <HowItWorksSection />
        <ResourcesSection />
        <BusinessSection />
        <PlansSection />
        <FaqSection />
      </main>
      <Footer />
    </div>
  );
}

export { App };
